import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Sun, Moon, Monitor, Languages } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useClickOutside } from '@/hooks/useClickOutside'
import { cn, scrollToSection } from '@/lib/utils'
import type { Theme } from '@/types'

const navItems = [
  { key: 'home', href: '#home' },
  { key: 'about', href: '#about' },
  { key: 'services', href: '#services' },
  { key: 'projects', href: '#projects' },
  { key: 'contact', href: '#contact' },
]

export function Header() {
  const { t, i18n } = useTranslation()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showThemeMenu, setShowThemeMenu] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  // Close theme menu when clicking outside
  const themeMenuRef = useClickOutside<HTMLDivElement>(
    useCallback(() => setShowThemeMenu(false), []),
    showThemeMenu
  )

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMenuOpen) return

    const menuElement = mobileMenuRef.current
    if (!menuElement) return

    const focusableElements = menuElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleTabKey)
    document.addEventListener('keydown', handleEscape)
    firstElement?.focus()

    return () => {
      document.removeEventListener('keydown', handleTabKey)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isMenuOpen])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  const handleNavClick = (href: string) => {
    const sectionId = href.replace('#', '')
    scrollToSection(sectionId)
    setIsMenuOpen(false)
  }

  const toggleLanguage = () => {
    const newLang = i18n.language === 'de' ? 'en' : 'de'
    i18n.changeLanguage(newLang)
  }

  const themeOptions: { value: Theme; icon: typeof Sun; label: string }[] = [
    { value: 'light', icon: Sun, label: t('theme.light') },
    { value: 'dark', icon: Moon, label: t('theme.dark') },
    { value: 'system', icon: Monitor, label: t('theme.system') },
  ]

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-background/80 backdrop-blur-lg shadow-sm border-b border-border'
          : 'bg-transparent'
      )}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick('#home')
            }}
            className="text-xl lg:text-2xl font-bold text-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            JF
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault()
                  handleNavClick(item.href)
                }}
                className="text-foreground-secondary hover:text-primary hover:-translate-y-0.5 active:scale-95 transition-all duration-200 font-medium"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Theme Selector */}
            <div className="relative" ref={themeMenuRef}>
              <button
                onClick={() => setShowThemeMenu(!showThemeMenu)}
                className="p-2 rounded-lg hover:bg-background-tertiary transition-colors"
                aria-label={t('accessibility.toggleTheme')}
                aria-expanded={showThemeMenu}
                aria-haspopup="true"
              >
                {resolvedTheme === 'dark' ? (
                  <Moon className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Sun className="w-5 h-5" aria-hidden="true" />
                )}
              </button>

              <AnimatePresence>
                {showThemeMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 py-2 w-36 bg-background-secondary rounded-lg shadow-lg border border-border"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    {themeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setTheme(option.value)
                          setShowThemeMenu(false)
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-background-tertiary transition-colors',
                          theme === option.value && 'text-primary'
                        )}
                        role="menuitem"
                      >
                        <option.icon className="w-4 h-4" aria-hidden="true" />
                        {option.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-tertiary transition-colors font-medium"
              aria-label={t('accessibility.toggleLanguage')}
            >
              <Languages className="w-5 h-5" aria-hidden="true" />
              <span className="uppercase text-sm">{i18n.language}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={menuButtonRef}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-background-tertiary transition-colors"
            aria-label={isMenuOpen ? t('accessibility.closeMenu') : t('accessibility.openMenu')}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6" aria-hidden="true" />
            )}
          </button>
        </div>

      </nav>

      {/* Mobile Menu - Fullscreen Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              onClick={() => setIsMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Slide-in Menu Panel */}
            <motion.div
              id="mobile-menu"
              ref={mobileMenuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed top-0 right-0 bottom-0 w-[min(85vw,320px)] bg-background z-50 shadow-2xl overflow-y-auto"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <span className="text-lg font-bold text-primary">Menu</span>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-3 rounded-xl hover:bg-background-tertiary transition-colors"
                  aria-label={t('accessibility.closeMenu')}
                >
                  <X className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="p-4 pb-48">
                <ul className="space-y-1">
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.key}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 + 0.1 }}
                    >
                      <a
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault()
                          handleNavClick(item.href)
                        }}
                        className="flex items-center gap-3 py-4 px-4 rounded-xl text-lg text-foreground hover:text-primary hover:bg-background-secondary active:bg-background-tertiary transition-all font-medium"
                      >
                        {t(`nav.${item.key}`)}
                      </a>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              {/* Settings Section */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-border bg-background-secondary"
              >
                {/* Theme Selection */}
                <div className="mb-4">
                  <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-2 block">
                    {t('theme.label') || 'Theme'}
                  </span>
                  <div className="flex items-center gap-2 bg-background rounded-xl p-1" role="group" aria-label="Theme selection">
                    {themeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTheme(option.value)}
                        className={cn(
                          'flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-lg transition-all font-medium text-sm',
                          theme === option.value
                            ? 'bg-primary text-white shadow-md'
                            : 'text-foreground-secondary hover:bg-background-tertiary active:bg-background-tertiary'
                        )}
                        aria-label={option.label}
                        aria-pressed={theme === option.value}
                      >
                        <option.icon className="w-4 h-4" aria-hidden="true" />
                        <span className="hidden sm:inline">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Language Toggle */}
                <button
                  onClick={toggleLanguage}
                  className="w-full flex items-center justify-center gap-3 py-4 px-4 rounded-xl bg-background hover:bg-background-tertiary active:bg-border transition-all font-medium"
                  aria-label={t('accessibility.toggleLanguage')}
                >
                  <Languages className="w-5 h-5" aria-hidden="true" />
                  <span>{i18n.language === 'de' ? 'Deutsch' : 'English'}</span>
                  <span className="ml-auto text-foreground-muted text-sm">
                    → {i18n.language === 'de' ? 'EN' : 'DE'}
                  </span>
                </button>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}
